<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 md:grid-cols-5 gap-3">
      <input v-model="filters.warehouse" type="text" placeholder="Filter by warehouse (optional)" class="input input-bordered w-full p-2 border rounded" @keyup.enter="reload()" />
      <select v-model.number="topWarehousesCount" class="p-2 border rounded">
        <option :value="3">Top 3 Warehouses</option>
        <option :value="4">Top 4 Warehouses</option>
        <option :value="5">Top 5 Warehouses</option>
        <option :value="6">Top 6 Warehouses</option>
      </select>
      <select v-model.number="pageSize" class="p-2 border rounded">
        <option :value="500">Page size: 500</option>
        <option :value="1000">Page size: 1000</option>
        <option :value="2000">Page size: 2000</option>
      </select>
      <div class="flex gap-2">
        <button class="px-3 py-2 bg-blue-600 text-white rounded" :disabled="loading" @click="reload">Show Charts</button>
        <button class="px-3 py-2 bg-gray-200 rounded" @click="reset">Reset</button>
      </div>
      <div class="flex items-center text-sm text-gray-500" v-if="loading">Loading…</div>
    </div>

    <p class="text-gray-500">Showing all items with actual stock greater than 0. Small warehouses are grouped into “Other”.</p>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <div v-for="card in cards" :key="card.item"
           class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-800 dark:text-gray-100">{{ card.item }}</h3>
          <span class="text-sm text-gray-500">Total: {{ number(card.totalQty) }}</span>
        </div>
        <div :ref="el => setChartRef(card.item, el)" style="width: 100%; height: 280px;"></div>
      </div>
    </div>
  </div>
  
</template>

<script>
import ApexCharts from 'apexcharts';

export default {
  name: 'AppStockQty',
  data() {
    return {
      loading: false,
      pageSize: 1000,
      topWarehousesCount: 4,
      filters: {
        warehouse: '',
      },
      cards: [],
      chartInstances: {},
      chartRefs: {},
    };
  },
  mounted() {
    this.initDarkModeListener();
    this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        const all = await this.fetchAllBins();
        this.cards = this.buildCards(all);
        await this.$nextTick();
        this.renderAllCharts();
      } catch (e) {
        console.error('Failed to load bins', e);
        this.cards = [];
        await this.$nextTick();
        this.renderAllCharts();
      } finally {
        this.loading = false;
      }
    },
    async fetchAllBins() {
      const results = [];
      let page = 1;
      while (true) {
        const params = new URLSearchParams();
        if (this.filters.warehouse) params.set('warehouse', this.filters.warehouse);
        params.set('min_actual_qty', '0'); // strictly > 0 on backend
        params.set('page', String(page));
        params.set('page_size', String(this.pageSize));
        const res = await fetch(`/erp/bins.json?${params.toString()}`, { credentials: 'same-origin' });
        const json = await res.json();
        const batch = Array.isArray(json.results) ? json.results : [];
        results.push(...batch);
        if (batch.length < this.pageSize) break;
        page += 1;
        if (page > 100) break; // safety cap
      }
      return results;
    },
    buildCards(rows) {
      // item -> warehouse -> qty
      const itemWarehouseQty = new Map();
      for (const r of rows) {
        const item = r.item_code || 'UNKNOWN_ITEM';
        const wh = r.warehouse || 'Unknown';
        const qty = Number(r.actual_qty) || 0;
        if (qty <= 0) continue;
        if (!itemWarehouseQty.has(item)) itemWarehouseQty.set(item, new Map());
        const m = itemWarehouseQty.get(item);
        m.set(wh, (m.get(wh) || 0) + qty);
      }

      // Build cards per item, grouping smaller warehouses into "Other" per item
      const cards = [];
      for (const [item, whMap] of itemWarehouseQty.entries()) {
        const sortedWarehouses = Array.from(whMap.entries()).sort((a, b) => b[1] - a[1]);
        const top = sortedWarehouses.slice(0, this.topWarehousesCount);
        const rest = sortedWarehouses.slice(this.topWarehousesCount);
        const otherSum = rest.reduce((s, [, v]) => s + v, 0);
        const categories = top.map(([w]) => w);
        const values = top.map(([, v]) => this.numberRaw(v));
        if (otherSum > 0) {
          categories.push('Other');
          values.push(this.numberRaw(otherSum));
        }
        const totalQty = values.reduce((a, b) => a + b, 0);
        cards.push({ item, categories, values, totalQty });
      }

      // Sort cards by total quantity desc
      cards.sort((a, b) => b.totalQty - a.totalQty);
      return cards;
    },
    setChartRef(item, el) {
      if (el) this.chartRefs[item] = el; else delete this.chartRefs[item];
    },
    renderAllCharts() {
      // Create/update charts for each card
      const seen = new Set();
      for (const card of this.cards) {
        const el = this.chartRefs[card.item];
        if (!el) continue;
        const options = this.getChartOptions(card.categories, card.values, card.item);
        if (this.chartInstances[card.item]) {
          this.chartInstances[card.item].updateOptions(options);
        } else {
          const chart = new ApexCharts(el, options);
          this.chartInstances[card.item] = chart;
          chart.render();
        }
        seen.add(card.item);
      }
      // Destroy charts no longer present
      for (const key of Object.keys(this.chartInstances)) {
        if (!seen.has(key)) {
          try { this.chartInstances[key].destroy(); } catch (e) {}
          delete this.chartInstances[key];
        }
      }
    },
    getChartOptions(categories, values, title) {
      return {
        chart: { type: 'bar', height: 260, background: 'transparent' },
        series: [{ name: 'Actual Qty', data: values }],
        xaxis: {
          categories,
          labels: { rotate: -45, style: { colors: this.isDark() ? '#9CA3AF' : '#4B5563' } },
        },
        yaxis: {
          labels: { style: { colors: this.isDark() ? '#9CA3AF' : '#4B5563' } },
        },
        legend: { show: false },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 1, colors: ['transparent'] },
        theme: { mode: this.isDark() ? 'dark' : 'light' },
        tooltip: { y: { formatter: (val) => this.number(val) } },
        title: { text: '', style: { color: this.isDark() ? '#F3F4F6' : '#111827' } },
      };
    },
    reset() {
      this.filters.warehouse = '';
      this.topWarehousesCount = 4;
      this.reload();
    },
    number(v) {
      if (v === null || v === undefined || isNaN(v)) return '';
      return Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 });
    },
    numberRaw(v) {
      if (v === null || v === undefined || isNaN(v)) return 0;
      return Number(v);
    },
    isDark() {
      return document.documentElement.classList.contains('dark');
    },
    initDarkModeListener() {
      document.addEventListener('dark-mode', () => {
        // Update all charts to apply theme changes
        for (const [key, chart] of Object.entries(this.chartInstances)) {
          const cats = chart.w?.config?.xaxis?.categories || [];
          const vals = chart.w?.config?.series?.[0]?.data || [];
          chart.updateOptions(this.getChartOptions(cats, vals, key));
        }
      });
    },
  },
};
</script>

<style scoped>
</style>
